import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { API_CONFIG, getApiUrl } from "../config/api";
import { useAuthenticatedFetch } from "../hooks/useAuthenticatedFetch";

interface TemplateItem {
  id: string;
  title: string;
  content: string;
  category?: string | null;
  createdAt: string;
  updatedAt: string;
}

interface PaginatedTemplatesResponse {
  data: TemplateItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

const DEFAULT_LIMIT = 10;

const Templates: React.FC = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const [items, setItems] = useState<TemplateItem[]>([]);
  const [search, setSearch] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [limit] = useState<number>(DEFAULT_LIMIT);
  const [total, setTotal] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [title, setTitle] = useState<string>("");
  const [content, setContent] = useState<string>("");
  const [category, setCategory] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);

  const lastQueryRef = useRef<string>("");
  const debounceTimerRef = useRef<number | null>(null);

  const canSubmit = useMemo(
    () => title.trim().length > 0 && content.trim().length > 0,
    [title, content]
  );

  const fetchTemplates = useCallback(
    async (params?: { page?: number; search?: string }) => {
      try {
        setLoading(true);
        setError(null);
        const currentPage = params?.page ?? page;
        const currentSearch = params?.search ?? search;
        const url = new URL(getApiUrl(API_CONFIG.ENDPOINTS.TEMPLATES));
        url.searchParams.set("page", String(currentPage));
        url.searchParams.set("limit", String(limit));
        if (currentSearch && currentSearch.trim().length > 0) {
          url.searchParams.set("search", currentSearch.trim());
        }
        const resp = await authenticatedFetch(url.toString(), {
          headers: { "Content-Type": "application/json" },
        });
        if (!resp.ok) {
          const data = await resp.json().catch(() => ({}));
          throw new Error(data?.message || "Ошибка при загрузке шаблонов");
        }
        const data: PaginatedTemplatesResponse = await resp.json();
        setItems(data.data);
        setTotal(data.total);
        setTotalPages(data.totalPages);
        setPage(data.page);
      } catch (e) {
        const message = e instanceof Error ? e.message : "Неизвестная ошибка";
        setError(message);
      } finally {
        setLoading(false);
      }
    },
    [authenticatedFetch, page, search, limit]
  );

  useEffect(() => {
    fetchTemplates();
  }, []);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearch(value);
    const queryKey = `${value}::${limit}`;
    if (debounceTimerRef.current) {
      window.clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = window.setTimeout(() => {
      if (lastQueryRef.current !== queryKey) {
        lastQueryRef.current = queryKey;
        fetchTemplates({ page: 1, search: value });
      }
    }, 350);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    try {
      setSubmitting(true);
      const body = {
        title: title.trim(),
        content: content.trim(),
        category: category.trim() ? category.trim() : null,
      };
      const resp = await authenticatedFetch(
        getApiUrl(API_CONFIG.ENDPOINTS.TEMPLATES),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }
      );
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data?.message || "Не удалось создать шаблон");
      }
      setTitle("");
      setContent("");
      setCategory("");
      await fetchTemplates({ page: 1, search });
    } catch (e) {
      const message = e instanceof Error ? e.message : "Неизвестная ошибка";
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Удалить шаблон?")) return;
    try {
      const resp = await authenticatedFetch(
        `${getApiUrl(API_CONFIG.ENDPOINTS.TEMPLATES)}/${id}`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
        }
      );
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data?.message || "Не удалось удалить шаблон");
      }
      await fetchTemplates({ page, search });
    } catch (e) {
      const message = e instanceof Error ? e.message : "Неизвестная ошибка";
      setError(message);
    }
  };

  const handlePageChange = (nextPage: number) => {
    const clamped = Math.min(Math.max(1, nextPage), totalPages);
    fetchTemplates({ page: clamped, search });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Шаблоны</h1>
          <p className="text-sm text-gray-500 mt-1">
            Создавайте и храните собственные шаблоны ответов
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="bg-white border rounded-xl shadow-sm">
            <div className="p-4 sm:p-5 border-b">
              <div className="flex items-center gap-3">
                <div className="relative flex-1">
                  <input
                    type="text"
                    value={search}
                    onChange={handleSearchChange}
                    placeholder="Поиск по названию или содержимому..."
                    className="input"
                  />
                </div>
              </div>
            </div>
            <div className="divide-y">
              {loading && (
                <div className="p-6 text-sm text-gray-500">Загрузка...</div>
              )}
              {error && !loading && (
                <div className="p-6 text-sm text-red-600">{error}</div>
              )}
              {!loading && items.length === 0 && (
                <div className="p-6 text-sm text-gray-500">
                  Шаблоны не найдены
                </div>
              )}
              {!loading &&
                items.map((tpl) => (
                  <div
                    key={tpl.id}
                    className="p-4 sm:p-5 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-base font-semibold text-gray-900">
                            {tpl.title}
                          </h3>
                          {tpl.category && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border">
                              {tpl.category}
                            </span>
                          )}
                        </div>
                        <p className="mt-1 text-sm text-gray-600 line-clamp-2">
                          {tpl.content}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {/* Update could be added later */}
                        <button
                          className="btn-secondary"
                          onClick={() => handleDelete(tpl.id)}
                        >
                          Удалить
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
            <div className="px-4 sm:px-5 py-3 border-t flex items-center justify-between text-sm">
              <div className="text-gray-500">Всего: {total}</div>
              <div className="flex items-center gap-2">
                <button
                  className="btn-secondary"
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page <= 1}
                >
                  Назад
                </button>
                <span className="text-gray-600">
                  Стр. {page} из {totalPages}
                </span>
                <button
                  className="btn-secondary"
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page >= totalPages}
                >
                  Далее
                </button>
              </div>
            </div>
          </div>
        </div>

        <div>
          <div className="bg-white border rounded-xl shadow-sm">
            <div className="p-4 sm:p-5 border-b">
              <h2 className="text-lg font-semibold">Новый шаблон</h2>
            </div>
            <form className="p-4 sm:p-5 space-y-4" onSubmit={handleSubmit}>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Название
                </label>
                <input
                  className="input mt-1"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Например: Ответ на негатив"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Категория (необязательно)
                </label>
                <input
                  className="input mt-1"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  placeholder="Например: негатив"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Содержимое
                </label>
                <textarea
                  className="input mt-1"
                  rows={6}
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Текст шаблона..."
                />
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={!canSubmit || submitting}
                >
                  {submitting ? "Сохранение..." : "Сохранить"}
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    setTitle("");
                    setContent("");
                    setCategory("");
                  }}
                >
                  Очистить
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Templates;
